import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { FormattedMessage } from 'react-intl'
import LazyHydrate from 'react-lazy-hydration'

import { useMediaQuery, Theme } from '@mui/material'

import Box from 'app/components/base/Box'
import GridList from 'app/components/base/GridList'
import Text from 'app/components/base/Text'
import ArticleCard from 'app/features/Blog/components/ArticleCard'
import TagPanel from 'app/features/Blog/TagPanel'
import { generateArticleLink } from 'app/utils/helpers/blog.helpers'

import BackLink from '~/components/buttons/BackLink'

import { useArticlesByTagView } from './hooks/useArticlesByTagView'

const EmptyBlog = dynamic(
  () => import('app/features/Blog/components/EmptyBlog'),
  {
    ssr: false
  }
)
const Pagination = dynamic(() => import('app/components/base/Pagination'), {
  ssr: false
})

const ArticlesByTagView = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const router = useRouter()

  const {
    articles,
    hasArticles,
    isEmptyBlog,
    isPageLoading,
    page,
    pagy,
    setPage,
    showPagination,
    tag
  } = useArticlesByTagView()

  return (
    <>
      <Box position="relative" mb={4}>
        <BackLink onClick={router.back} />

        <Text
          variant="h2"
          typography={{ xs: 'h3', lg: 'h2' }}
          sx={{ textAlign: 'center' }}
        >
          <FormattedMessage defaultMessage="Search by Tag" />
        </Text>
      </Box>

      <TagPanel readonly tag={tag} withIcon sx={{ mb: 4.25 }} />

      {isEmptyBlog && <EmptyBlog />}

      {hasArticles && (
        <Box sx={{ maxWidth: { xs: '500px', sm: '100%' }, mx: 'auto' }}>
          <GridList
            items={articles || []}
            columns={{ xs: 1, sm: 2, md: 3 }}
            spacing={4}
            keyExtractor={({ id }) => id}
            renderItem={(item: BlogArticleModel, index) => {
              const link = generateArticleLink({
                slug: item.slug,
                categorySlug: item.blog_category?.slug
              })

              return (
                <LazyHydrate whenVisible>
                  <ArticleCard
                    loading={isPageLoading}
                    link={link}
                    image={item.title_image_url}
                    title={item.title}
                    createdAt={item.created_at}
                    imageAltText={item?.title_image?.alt_text}
                    index={index}
                    isMobile={isMobile}
                  />
                </LazyHydrate>
              )
            }}
          />
        </Box>
      )}

      {showPagination && (
        <LazyHydrate whenVisible>
          <Pagination
            count={pagy?.pages}
            page={page}
            onChange={setPage}
            sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}
          />
        </LazyHydrate>
      )}
    </>
  )
}

export default ArticlesByTagView
